/**
 * Validation Results Accordion Component
 * Displays the results array for a validation run in an expandable format
 */
import React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Link,
  Typography,
  Paper
} from '@mui/material'
import { formatDateToDDMMYY } from '../../../constants/utils'
import { emailAdValidationStyles } from '../styles'

const styles = emailAdValidationStyles

const ValidationResultsAccordion = ({ results = [] }) => {
  const getStatusChip = status => {
    const statusStyles = {
      COMPLETED: styles.completedChip,
      IN_PROGRESS: styles.runningChip,
      FAILED: styles.failedChip
    }

    const statusLabels = {
      COMPLETED: 'COMPLETED',
      IN_PROGRESS: 'IN PROGRESS',
      FAILED: 'FAILED'
    }

    return (
      <Chip
        label={statusLabels[status] || status}
        size="small"
        style={{
          ...styles.statusChip,
          ...statusStyles[status],
          fontSize: '0.75rem',
          height: '24px'
        }}
      />
    )
  }

  const formatDateTime = dateString => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return `${formatDateToDDMMYY(date)} ${date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit'
    })}`
  }

  if (!results || results.length === 0) {
    return (
      <Box sx={{ padding: 2, textAlign: 'center', color: '#666' }}>
        <Typography variant="body2">No results available</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ margin: '8px 0', backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e1e5e9' }}>
      <Typography
        variant="subtitle2"
        sx={{
          padding: '12px 16px 8px 16px',
          fontWeight: 600,
          color: '#1a202c',
          borderBottom: '1px solid #e2e8f0',
          backgroundColor: '#f7fafc'
        }}
      >
        Validation Results ({results.length})
      </Typography>
      <TableContainer component={Paper} elevation={0} sx={{ boxShadow: 'none', backgroundColor: 'transparent' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#edf2f7' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#2d3748', borderBottom: '1px solid #e2e8f0' }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#2d3748', borderBottom: '1px solid #e2e8f0' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#2d3748', borderBottom: '1px solid #e2e8f0' }}>Errors</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#2d3748', borderBottom: '1px solid #e2e8f0' }}>Created By</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#2d3748', borderBottom: '1px solid #e2e8f0' }}>Created At</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#2d3748', borderBottom: '1px solid #e2e8f0' }}>Result Sheet</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {results.map((result, index) => (
              <TableRow
                key={result.id || index}
                sx={{
                  '&:nth-of-type(odd)': { backgroundColor: '#ffffff' },
                  '&:nth-of-type(even)': { backgroundColor: '#f8fafc' },
                  '&:hover': { backgroundColor: '#ebf4ff' },
                  borderBottom: '1px solid #e2e8f0'
                }}
              >
                <TableCell sx={{ fontSize: '0.8rem', padding: '8px 12px', borderBottom: '1px solid #e2e8f0', color: '#4a5568' }}>
                  {result.id}
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem', padding: '8px 12px', borderBottom: '1px solid #e2e8f0' }}>
                  {getStatusChip(result.status)}
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem', padding: '8px 12px', maxWidth: '200px', borderBottom: '1px solid #e2e8f0' }}>
                  {result.errors ? (
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#e53e3e',
                        fontSize: '0.75rem',
                        wordBreak: 'break-word',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                      title={result.errors}
                    >
                      {result.errors}
                    </Typography>
                  ) : (
                    <span style={{ color: '#a0aec0', fontSize: '0.75rem' }}>No errors</span>
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem', padding: '8px 12px', borderBottom: '1px solid #e2e8f0', color: '#4a5568' }}>
                  {result.created_by_email || '-'}
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem', padding: '8px 12px', borderBottom: '1px solid #e2e8f0', color: '#4a5568' }}>
                  {formatDateTime(result.created_at)}
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem', padding: '8px 12px', borderBottom: '1px solid #e2e8f0' }}>
                  {result.result_sheet_url ? (
                    <Link
                      href={
                        result.result_sheet_url.startsWith('http')
                          ? result.result_sheet_url
                          : `https://${result.result_sheet_url}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        fontSize: '0.75rem',
                        textDecoration: 'none',
                        color: '#3182ce',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      View Sheet
                    </Link>
                  ) : (
                    <span style={{ color: '#a0aec0', fontSize: '0.75rem' }}>No Sheet</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

ValidationResultsAccordion.propTypes = {
  results: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      status: PropTypes.string,
      errors: PropTypes.string,
      created_by_email: PropTypes.string,
      created_at: PropTypes.string,
      result_sheet_url: PropTypes.string
    })
  )
}

export default ValidationResultsAccordion
